import { createContext, useContext, useState, useEffect, ReactNode } from "react";
import { translations, Language } from "@/i18n/translations";

type Translations = typeof translations.en;

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: Translations;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export const LanguageProvider = ({ children }: { children: ReactNode }) => {
  const [language, setLanguageState] = useState<Language>(() => {
    // Check if user has previously saved a preference
    const saved = localStorage.getItem("language") as Language;
    if (saved && ["en", "nl", "fr"].includes(saved)) {
      return saved;
    }
    // Auto-detect from browser languages (checks all preferred languages)
    const browserLanguages = navigator.languages || [navigator.language];
    for (const lang of browserLanguages) {
      const code = lang.split("-")[0].toLowerCase();
      if (code === "nl") return "nl";
      if (code === "fr") return "fr";
      // Check region codes: nl-BE, fr-BE, fr-FR, etc.
      const region = lang.split("-")[1]?.toUpperCase();
      if (region === "BE" && code === "nl") return "nl";
      if (region === "BE" && code === "fr") return "fr";
    }
    return "en";
  });

  const setLanguage = (lang: Language) => {
    setLanguageState(lang);
    localStorage.setItem("language", lang);
    document.documentElement.lang = lang;
  };

  useEffect(() => {
    document.documentElement.lang = language;
  }, [language]);

  const t = translations[language] as Translations;

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error("useLanguage must be used within a LanguageProvider");
  }
  return context;
};
